<template>
  <main class="work-time-account-journal">
    <h1>Журнал учета рабочего времени</h1>
    <form
      class="work-time-account-journal__filters"
      v-if="hasFetchedOptions"
      v-on:submit.prevent
    >
      <date-range-with-presets
        v-bind:presets="presetsOfDates"
        v-bind:value="dates"
        custom-color-date="black"
        v-on:input="onInputDates" />
      <multiple-autocomplete-input
        class="work-time-account-journal__employees-filter"
        label="Сотрудники"
        removal-per-chip
        v-bind:items="employeeOptions"
        v-bind:value="selectedEmployees"
        v-on:input="onInputEmployees" />
    </form>
    <router-link
      class="work-time-account-journal__link"
      v-bind:to="{
        name: 'ClinicPanelAppointmentScheduleEmployees',
        params: { shownWorkTimeControlsDialog: true },
      }">
      Включить учет времени
    </router-link>
    <section class="work-time-account-journal__body">
      <em v-if="isFetchingJournal" class="work-time-account-journal__result">Идет построение журнала...</em>
      <template v-else-if="hasBodyLines">
        <table class="work-time-account-journal__table">
          <thead class="work-time-account-journal__table__header">
            <tr>
              <th
                v-bind:key="title"
                v-for="{ title } in columns"
                class="work-time-account-journal__table__header__cell"
              >{{ title }}</th>
            </tr>
          </thead>
          <tbody class="work-time-account-journal__table__body">
            <tr
              v-bind:key="line.key"
              v-for="line in bodyLines"
              class="work-time-account-journal__table__body__line"
            >
              <td
                v-bind:key="`${line.key}:${field}`"
                v-for="{ field, format } in columns"
                class="work-time-account-journal__table__body__cell"
              >
                <template v-if="'title' === field && line.showMore">
                  <label
                    title="Показать по дням"
                    class="work-time-account-journal__table__expansion"
                  >
                    <input
                      type="checkbox"
                      v-bind:value="line.key"
                      v-model="expandedLines">
                    <span />
                  </label>
                </template>
                <template v-if="'list' === format">
                  <ul class="list-cell">
                    <li
                      class="list-cell__line"
                      v-bind:key="`${line.key}:${field}:${item}`"
                      v-for="item in line[field]">{{ item }}</li>
                  </ul>
                </template>
                <template v-else>
                  {{ line[field] }}
                </template>
                <template v-if="'time' === field && line.showRemovingOfLastCounter">
                  <button
                    title="Удалить последний остановленный счетчик"
                    type="button"
                    class="work-time-account-journal__table__delete"
                    v-on:click="onRemoveLastCounter(line.employee_id)"
                  />
                </template>
              </td>
            </tr>
          </tbody>
        </table>
        <button
          class="work-time-account-journal__excel"
          type="button"
          v-on:click="exportToExcel"
        >Выгрузить в Excel</button>
      </template>
      <em v-else-if="dates[1]">Не удалось найти счетчиков рабочего времени за период</em>
    </section>
  </main>
</template>
<script>
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { getISODateStringByDate } from '@/mixins/dateMixin';

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

/**
 * @description Раздел журнала учета рабочего времени.
 * @type {Vue}
 */
export default {
  components: {
    DateRangeWithPresets,
    MultipleAutocompleteInput,
  },
  computed: {
    /**
     * @description Строки тела журнала.
     * @type {object[]}
     */
    bodyLines() {
      const render = (path) => (item, index) => {
        const key = [...path, index].join(':');
        const showMore = Object.hasOwn(item, 'days');
        const showRemovingOfLastCounter = !showMore && (index === 0);
        const line = {
          ...item,
          key,
          showMore,
          showRemovingOfLastCounter,
        };
        if (showMore && this.expandedLines.includes(key)) {
          return [line, ...item.days.flatMap(render([...path, index]))];
        }
        return [line];
      };
      return this.employees.flatMap(render(['employee']));
    },
    /**
     * @description Имеет строки журнала.
     * @type {boolean}
     */
    hasBodyLines() {
      return this.bodyLines.length !== 0;
    },
    /**
     * @description Набор предварительных дат для фильтрации.
     * @type {object[]}
     */
    presetsOfDates() {
      return this
        .periodOptions
        .map(({ from_to: [from, to], title, value }) => ({
          periodDates: [new Date(from), new Date(to)],
          title,
          value,
        }))
        .map(({ periodDates, title, value }) => ({
          id: value,
          label: title,
          title,
          toValue: () => periodDates,
        }));
    },
  },
  created() {
    this
      .fetchJournalOptions()
      .then(() => {
        const todayDate = new Date(this.today);
        this.dates = [todayDate, todayDate];
        this.fetchJournal();
      })
      .finally(() => { this.$nextTick(() => { this.hasFetchedOptions = true; }); });
  },
  data() {
    return {
      columns: [],
      dates: [],
      employeeOptions: [],
      employees: [],
      expandedLines: [],
      hasFetchedOptions: false,
      isFetchingJournal: false,
      periodOptions: [],
      selectedEmployees: [],
      today: null,
    };
  },
  methods: {
    /**
     * @description Экспортировать журнал по выбранному периоду в Excel таблицу.
     * @returns {void}
     */
    exportToExcel() {
      const [startsDate, endsDate] = this.dates.map(getISODateStringByDate);
      this
        .$workTimeControlsGateway
        .downloadWorkTimeAccountJournalExcelSpreadsheet(
          this.selectedEmployees,
          startsDate,
          endsDate,
        )
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach((file) => { saveFile(file, 'Учет времени.xls'); });
        });
    },
    /**
     * @description Получить журнал по выбранному периоду.
     * @returns {void}
     */
    fetchJournal() {
      const [startsDate, endsDate] = this.dates.map(getISODateStringByDate);
      this.isFetchingJournal = true;
      this
        .$workTimeControlsGateway
        .viewWorkTimeAccountJournal(this.selectedEmployees, startsDate, endsDate)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ columns, employees }) => {
            this.columns = columns;
            this.employees = employees;
            this.expandedLines = [];
          });
        })
        .finally(() => { this.isFetchingJournal = false; });
    },
    /**
     * @description Получить опции журнала.
     * @returns {Promise}
     */
    fetchJournalOptions() {
      return this
        .$workTimeControlsGateway
        .viewWorkTimeJournalOptions()
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ employees, periods, today }) => {
            this.employeeOptions = employees;
            this.periodOptions = periods;
            this.today = today;
          });
        });
    },
    /**
     * @description Обработать выбор периода для журнала.
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.dates = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.fetchJournal();
      } else {
        this.dates = value;
      }
    },
    /**
     * @description Обработать ввод сотрудников фильтрации.
     * @returns {void}
     */
    onInputEmployees(value) {
      this.selectedEmployees = value;
      if (this.dates.length === 2) {
        this.fetchJournal();
      }
    },
    /**
     * @description Обработать удаление последнего счетчика.
     * @param {number} employeeId Идентификатор сотрудника.
     * @returns {void}
     */
    onRemoveLastCounter(employeeId) {
      this
        .showRemoveDialog('Вы действительно хотите удалить последний счетчик?')
        .then(({ result }) => new Promise((resolve) => {
          if (DialogResult.DELETE === result) {
            resolve();
          }
        }))
        .then(() => {
          this
            .$workTimeControlsGateway
            .removeWorkLastCounter(employeeId)
            .then((result) => {
              result.forEachLeft(({ title }) => { this.notifyFail(title); });
              result.forEach(({ title }) => {
                this.notifySuccess(title);
                this.fetchJournal();
              });
            });
        });
    },
  },
  mixins: [
    builtInDialogMixin,
    notifierMixin,
  ],
  name: 'ClinicPanelWorkTimeAccountJournal',
};
</script>
<style lang="scss">
@use "@/scss/buttons";

.list-cell {
  display: inline-block;
  list-style-type: none;
  & &__line {
    font-size: .8rem;
    line-height: 1.5rem;
  }
  & &__line:nth-child(2n) {
    color: var(--unfocus-color);
    font-weight: 700;
  }
}

.work-time-account-journal {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 31px 20px 48px 23px;
  &__link:hover {
    text-decoration: none;
  }
  &__employees-filter {
    width: min(100%, 40rem);
    .menuable__content__active {
      min-width: 400px !important;
      @media screen and (max-width: 480px) {
        min-width: 250px !important;
      }
    }
  }
  & &__excel {
    @include buttons.btn-transparent(10rem, 3rem, 1rem);
    margin-top: 1rem;
  }
  &__filters {
    align-items: baseline;
    display: flex;
    flex-direction: row;
    gap: 2rem;
    margin: 20px 0 20px 0;
  }
  &__link {
    display: flex;
    margin-bottom: 30px;
  }
  &__result {
    font-size: 14px;
    font-weight: 600;
  }
  &__table {
    border-collapse: collapse;
    font-size: 14px;
    line-height: 16px;
    overflow: auto;
    table-layout: fixed;
    width: 100%;
    &__header {
      border-bottom: 2px solid #0489E0;
      font-weight: 700;
      &__cell {
        padding: 10px 8px;
        text-align: left;
      }
    }
    &__body {
      &__cell {
        padding: 15px 8px;
        text-align: left;
        white-space: nowrap;
        text-transform: none;
        .list-cell {
          padding-left: 0;
        }
      }
      &__line {
        border-bottom: 1px solid #EAEFF3;
        &:first-child {
          text-transform: uppercase;
          letter-spacing: 0.14px;
        }
      }
    }
    &__expansion {
      position: relative;
      &>input {
        position: absolute;
        z-index: -1;
        opacity: 0;
        &:checked+span {
          transform: rotate(180deg);
          background-position: top;
        }
      }
      &>span {
        display: inline-flex;
        align-items: center;
        user-select: none;
        cursor: pointer;
        background-image: url("@/assets/img/checked/arrow-top-blue.svg");
        background-size: contain;
        background-position: bottom;
        width: 15px;
        height: 15px;
        margin: 0 17px 0 0;
      }
    }
    &__delete {
      background-image: url("@/assets/img/exit-black.svg");
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      cursor: pointer;
      width: 12px;
      height: 12px;
      margin: 0 0 0 10px;
      transform: translateY(1px);
    }
  }
}
@media screen and (max-width: 800px) {
  .work-time-account-journal {
    &__body {
      overflow: scroll;
    }
    &__filters {
      flex-direction: column;
    }
    &__table {
      width: 800px;
    }
  }
}
</style>
